<template>
  <TheMainContent class="bg-surface-default-low pt-32 md:pt-64">
    <RevContainer>
      <div class="mx-auto max-w-[736px]">
        <p class="punchline-sans text-center">
          {{ i18n(translations.pageTitle) }}
        </p>
        <h1 class="heading-3 text-static-default-low mt-16 text-center">
          {{
            i18n(translations.resultsTitle, {
              firstModel: stableLabels[0],
              secondModel: stableLabels[1],
            })
          }}
        </h1>
        <div v-if="stableCategory" class="mt-16 flex justify-center">
          <RevButton
            :to="stableCategory.url"
            variant="primary"
            @click="onCategoryClick"
          >
            {{
              i18n(translations.shopCategory, {
                category: stableCategory.label,
              })
            }}
          </RevButton>
        </div>
        <Reassurance class="mt-56 text-center" />
        <div
          ref="heroSwitchers"
          class="mx-auto mt-56 grid grid-cols-2 gap-x-24 lg:gap-x-40"
          :inert="isStickyVisible"
        >
          <ProductSwitcher
            v-for="index in [0, 1] as const"
            :id="`product-switcher-${index}`"
            :key="index"
            class="mb-24"
            :label="stableLabels[index]"
            :options="optionsPerProduct[index] ?? []"
            @switch="onSwitchProduct(index, $event)"
          />
        </div>
      </div>
    </RevContainer>

    <RouterView />

    <!-- Rendered here (not in the page) so the sticky logic can observe it directly. -->
    <div ref="relatedContentSentinel" />
    <RelatedContent />

    <RevContainer class="fixed inset-x-0 top-0 z-[1]">
      <div class="mx-auto max-w-[736px]">
        <Transition
          enter-active-class="motion-safe:ease-out motion-safe:duration-150"
          enter-from-class="motion-safe:-translate-y-full"
          enter-to-class="motion-safe:translate-y-0"
          leave-active-class="motion-safe:ease-in motion-safe:duration-150"
          leave-from-class="motion-safe:translate-y-0"
          leave-to-class="motion-safe:-translate-y-full"
        >
          <div
            v-show="isStickyVisible"
            :aria-label="i18n(translations.stickyHeader)"
            class="bg-surface-default-low border-static-default-low border-b md:border-b-0"
            role="region"
          >
            <div
              class="mx-auto grid grid-cols-2 gap-x-24 pb-12 pt-16 lg:gap-x-40 lg:pb-16"
            >
              <div
                v-for="(product, index) in stableProducts"
                :key="`sticky-card-${product.productId}`"
              >
                <ProductSwitcher
                  :id="`product-switcher-sticky-${index}`"
                  class="mb-16"
                  :label="stableLabels[index]"
                  :options="optionsPerProduct[index] ?? []"
                  @switch="onSwitchProduct(index, $event)"
                />
                <ProductCard
                  :loading="switchingProductIndex === index && enrichPending"
                  :product
                  sticky
                  @product-click="onProductClick(index, $event)"
                />
              </div>
            </div>
            <!-- v-if (not v-show): RevTabs measures offsetLeft/Width on
                 mount for its active-bar — both are 0 while display:none. -->
            <div class="hidden overflow-hidden md:block">
              <Tabs
                v-if="isStickyVisible"
                :anchors
                class="-mb-8"
                @tab-click="trackTabClick"
              />
            </div>
          </div>
        </Transition>
      </div>
    </RevContainer>
  </TheMainContent>
</template>

<script lang="ts" setup>
import { useRoute, useRouter, useState } from '#imports'
import { computed, ref, toRef, useTemplateRef, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevContainer } from '@ds/components/Container'
import { useMediaQuery, useWindowScroll } from '@vueuse/core'

import TheMainContent from '~/components/TheMainContent/TheMainContent.vue'
import { useHideOnScroll } from '~/scopes/navigation/composables/useHideOnScroll'

import ProductCard from '../../components/Results/ProductCard/ProductCard.vue'
import ProductSwitcher from '../../components/Results/ProductSwitcher/ProductSwitcher.vue'
import Reassurance from '../../components/Results/Reassurance/Reassurance.vue'
import RelatedContent from '../../components/Results/RelatedContent/RelatedContent.vue'
import Tabs from '../../components/Results/Tabs/Tabs.vue'
import { useCompareClickTracking } from '../../composables/useCompareClickTracking/useCompareClickTracking'
import { useCompareTracking } from '../../composables/useCompareTracking/useCompareTracking'
import { useComparisonOptions } from '../../composables/useComparisonOptions/useComparisonOptions'
import { useEnrichResultsPage } from '../../composables/useEnrichResultsPage/useEnrichResultsPage'
import { useFetchResultsPage } from '../../composables/useFetchResultsPage/useFetchResultsPage'
import { useStickyRouteParam } from '../../composables/useStickyRouteParam'
import { MODELS_COMPARISON } from '../../routes-names'

import translations from './ResultsLayout.translations'

const route = useRoute()
const router = useRouter()
const i18n = useI18n()

const slugA = useStickyRouteParam('productA')
const slugB = useStickyRouteParam('productB')

const { optionsPerProduct } = useComparisonOptions(slugA, slugB)

const { data: comparisonData, pending: comparisonPending } =
  await useFetchResultsPage(slugA, slugB)
const {
  products,
  category,
  anchors,
  pending: enrichPending,
  categoryRequest,
} = useEnrichResultsPage(toRef(comparisonData), toRef(comparisonPending))

await categoryRequest

// Populates the shared compare-tracking context read by child trackers.
useCompareTracking({ products, category, slugA, slugB })
const {
  trackProductClick,
  trackCategoryClick,
  trackSwitcherClick,
  trackTabClick,
} = useCompareClickTracking()

const switchingProductIndex = useState<0 | 1 | null>(
  'comparison-switching-index',
  () => null,
)
watch(enrichPending, (pending) => {
  if (!pending) switchingProductIndex.value = null
})

// Last-known values, refreshed only when the next fetch settles, so cards /
// labels / category button don't flash back to empty during a product switch.
const stableProducts = ref(products.value)
watch(products, (p) => {
  if (p.length > 0) stableProducts.value = p
})

const labelsFromData = computed<[string, string]>(() => [
  comparisonData.value?.products[0]?.title ?? slugA.value,
  comparisonData.value?.products[1]?.title ?? slugB.value,
])
const stableLabels = ref<[string, string]>(labelsFromData.value)
watch(labelsFromData, (val) => {
  if (val[0] !== slugA.value && val[1] !== slugB.value) stableLabels.value = val
})

const stableCategory = ref(category.value)
watch(category, (val) => {
  if (val !== undefined) stableCategory.value = val
})

const { hidden: isHeaderHidden } = useHideOnScroll()

const { y: scrollY } = useWindowScroll()
const isDesktop = useMediaQuery('(min-width: 768px)')

const heroSwitchers = useTemplateRef<HTMLElement>('heroSwitchers')
const relatedContentSentinel = useTemplateRef<HTMLElement>(
  'relatedContentSentinel',
)

// Approx in-page cards height: only show the sticky once they're scrolled past,
// otherwise the cards would appear twice.
const CARDS_OFFSET_DESKTOP = 460
const CARDS_OFFSET_MOBILE = 250

const isScrolled = ref(false)
const isAtBottom = ref(false)
watch(scrollY, () => {
  const hero = heroSwitchers.value
  if (hero) {
    const cardsOffset = isDesktop.value
      ? CARDS_OFFSET_DESKTOP
      : CARDS_OFFSET_MOBILE
    isScrolled.value = hero.getBoundingClientRect().bottom < -cardsOffset
  }

  const related = relatedContentSentinel.value
  if (related) {
    // `+ 4` absorbs sub-pixel landings at end-of-scroll.
    const stickyHeight = isDesktop.value ? 202 : 196
    isAtBottom.value = related.getBoundingClientRect().top <= stickyHeight + 4
  }
})

const isStickyVisible = computed(
  () => isScrolled.value && !isAtBottom.value && isHeaderHidden.value,
)

function onProductClick(index: number, payload: { ctaLabel: 'see_options' }) {
  const product = stableProducts.value[index]
  if (!product) return

  trackProductClick({
    product,
    position: index + 1,
    listName: 'compare_sticky_header',
    ctaLabel: payload.ctaLabel,
  })
}

function onCategoryClick() {
  if (stableCategory.value) trackCategoryClick(stableCategory.value)
}

function onSwitchProduct(index: number, option: { value: string }) {
  trackSwitcherClick({
    previousProduct: stableProducts.value[index],
    position: index + 1,
    newSlug: option.value,
  })

  switchingProductIndex.value = index as 0 | 1
  const newSlugA = index === 0 ? option.value : slugA.value
  const newSlugB = index === 1 ? option.value : slugB.value

  // `#scroll=false` is consumed by `scrollBehavior` in `router.options.ts` to
  // skip the auto scroll-to-top — keeps the viewport anchored when switching
  // one product, while every other navigation (carousels, etc.) still scrolls.
  router.push({
    name: MODELS_COMPARISON.RESULTS,
    params: {
      locale: route.params.locale,
      productA: newSlugA,
      productB: newSlugB,
    },
    hash: '#scroll=false',
  })
}
</script>
